/**
 * הסרגל התחתון — בכל מצב ובכל רוחב (6ו-ב, D31, A-184, A-185; מחליף את visits/ActionBar.tsx ואת
 * לשוניות הטלפון של App.tsx).
 *
 * `nav.action-bar.material` אחד, מרונדר פעם אחת מהשלד (App.tsx) **מחוץ לעטיפת התוכן**: עטיפה
 * שמקבלת `transform` כשחלונית פתוחה (חלק 5, A-187) הופכת ל־containing block של צאצאיה
 * הקבועים, והסרגל (`position: fixed; bottom: 0`) היה ״נופל״ לתחתית המסמך. קבוע בתחתית החלון,
 * 56px (--bar-h) + אזור הבית, שורה אחת של עד חמישה חריצים: אייקון 22px מעל מילה, או גלולה
 * מודגשת (`is-tinted`) ל״הבא שלא נבדק״. כיתוב שלא נכנס נחתך ב־ellipsis — לעולם לא שתי שורות.
 *
 * - **חריץ הקשר** (`BottomBarContext` / `InBottomBar`, כמו `SideSlotContext` / `InSideNav`):
 *   מסך שמציב תוכן דרך `InBottomBar` (portal) מחליף את ברירת המחדל — שלוש הלשוניות (ביקורים ·
 *   תיק אתר · חשבון; `role=tablist`, tabindex נודד + חצים, A-175). `claims` סופר את המציבים;
 *   כל עוד יש אחד — הלשוניות אינן מרונדרות, ולכן בכל רגע יש במסמך קבוצת `role=tab` אחת בלבד.
 * - **מוסתר** (`hidden`, לא מוסר — הריפוד של `.app` לא קופץ, A-167) בפוקוס בשדה טקסט
 *   (`useTextFocus`, בכל מסך — אחרת המקלדת מכסה שדה והסרגל מעליה) וכשמסך מבקש זאת
 *   (`InBottomBar hidden` — חלון התוכנית פתוח, 6ו-ג; לחלון פסים משלו). התוכן נשאר בסרגל המוסתר.
 * - **תנועה** (A-189): יציאה ב־`translateY(100%)` + opacity ב־160ms ואז `hidden`; בהצגה —
 *   `hidden` יורד ואז הכניסה מאותו מקום. תנועה מופחתת — מיידי. רק transform/opacity זזים.
 * - ״חומר״ (A-154): צל רק כשיש תוכן מתחת — sentinel בסוף התוכן (App.tsx) + useStuck.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { moveWithArrows, TAB_ITEMS } from './a11y.ts';
import { Icon, type IconName } from './Icon.tsx';
import { prefersReducedMotion } from './motion.ts';
import { useStuck } from './useStuck.ts';

/** שלושת המסכים הראשיים — הלשוניות של ברירת המחדל. */
export type Tab = 'visits' | 'site' | 'account';

const TABS: ReadonlyArray<{ id: Tab; label: string; icon: IconName; testId: string }> = [
  { id: 'visits', label: 'ביקורים', icon: 'list', testId: 'tab-visits' },
  { id: 'site', label: 'תיק אתר', icon: 'folder', testId: 'tab-site' },
  { id: 'account', label: 'חשבון', icon: 'user', testId: 'tab-account' },
];

/** משך תנועת היציאה — כמו ב־CSS (`.action-bar`). */
const HIDE_MS = 160;

/** האם הפקד הממוקד הוא שדה טקסט — המקלדת של הטלפון פתוחה. */
function isTextField(el: Element | null): boolean {
  if (el === null) return false;
  const tag = el.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    (el as HTMLElement).isContentEditable
  );
}

/**
 * עוקב אחרי פוקוס בשדה טקסט בכל המסמך. נבדק מחדש גם אחרי לחיצה: שדה ממוקד שהוסר מהמסמך
 * (מעבר מסך) אינו שולח focusout, והסרגל היה נשאר מוסתר עד אירוע הפוקוס הבא.
 */
export function useTextFocus(): boolean {
  const [focused, setFocused] = useState(() =>
    typeof document === 'undefined' ? false : isTextField(document.activeElement),
  );
  useEffect(() => {
    const update = () => setFocused(isTextField(document.activeElement));
    const events = ['focusin', 'focusout', 'click'] as const;
    for (const ev of events) document.addEventListener(ev, update);
    return () => {
      for (const ev of events) document.removeEventListener(ev, update);
    };
  }, []);
  return focused;
}

interface BottomBarSlot {
  /** ה־nav של הסרגל — יעד ה־portal; null לפני שהשלד הוצב. */
  slot: HTMLElement | null;
  /** תפיסת הסרגל: ברירת המחדל יורדת; `hidden` — הסרגל כולו מוסתר. מחזיר שחרור. */
  claim: (opts: { hidden: boolean }) => () => void;
}

export const BottomBarContext = createContext<BottomBarSlot>({
  slot: null,
  claim: () => () => {},
});

/** מה שהסרגל עצמו קורא מהספק: תפוס? מוסתר בכוח? ואיפה לרשום את ה־nav. */
interface BarState {
  occupied: boolean;
  forceHidden: boolean;
  setSlot: (el: HTMLElement | null) => void;
}

const BarStateContext = createContext<BarState>({
  occupied: false,
  forceHidden: false,
  setSlot: () => {},
});

/**
 * הספק — עוטף את כל השלד (App.tsx): המסכים (צאצאי עטיפת התוכן) מציבים דרך `InBottomBar`,
 * והסרגל (אח של העטיפה) קורא את המצב. המונים כאן ולא ב־BottomBar כי הקשר זורם רק כלפי מטה.
 */
export function BottomBarProvider({ children }: { children: ReactNode }): ReactElement {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const [claims, setClaims] = useState(0);
  const [hiddenClaims, setHiddenClaims] = useState(0);
  const claim = useCallback(({ hidden }: { hidden: boolean }) => {
    setClaims((n) => n + 1);
    if (hidden) setHiddenClaims((n) => n + 1);
    return () => {
      setClaims((n) => n - 1);
      if (hidden) setHiddenClaims((n) => n - 1);
    };
  }, []);
  const value = useMemo(() => ({ slot, claim }), [slot, claim]);
  const state = useMemo(
    () => ({ occupied: claims > 0, forceHidden: hiddenClaims > 0, setSlot }),
    [claims, hiddenClaims],
  );
  return (
    <BottomBarContext.Provider value={value}>
      <BarStateContext.Provider value={state}>{children}</BarStateContext.Provider>
    </BottomBarContext.Provider>
  );
}

/**
 * מציב תוכן בסרגל במקום ברירת המחדל. `hidden` — תופס את הסרגל ומסתיר אותו (חלון התוכנית פתוח).
 * ההרשמה ב־layout effect: הלשוניות יורדות לפני הציור של המסגרת שבה התוכן נכנס — אין מסגרת מצוירת
 * עם שניהם.
 *
 * 6ו-ג (A-193): גם כשהסרגל מוסתר התוכן נשאר בו (ה־nav הוא שמקבל `hidden`, כמו בפוקוס בשדה) — הכפתור
 * שפתח את החלון (״תוכנית״) נשאר אותו אלמנט, והפוקוס חוזר אליו כשהסרגל חוזר; והסרגל יוצא עם תוכנו
 * ולא ריק.
 */
export function InBottomBar({
  children,
  hidden = false,
}: {
  children: ReactNode;
  hidden?: boolean;
}): ReactElement | null {
  const { slot, claim } = useContext(BottomBarContext);
  useLayoutEffect(() => claim({ hidden }), [claim, hidden]);
  return slot !== null ? createPortal(children, slot) : null;
}

type Phase = 'shown' | 'leaving' | 'hidden' | 'entering';

/** הצגה/הסתרה עם תנועה (A-189). בטעינה — בלי תנועה; בתנועה מופחתת — מיידי. */
function useBarPhase(wantHidden: boolean): Phase {
  const [phase, setPhase] = useState<Phase>(wantHidden ? 'hidden' : 'shown');
  const previous = useRef(wantHidden);
  useEffect(() => {
    if (previous.current === wantHidden) return;
    previous.current = wantHidden;
    if (prefersReducedMotion()) {
      setPhase(wantHidden ? 'hidden' : 'shown');
      return;
    }
    if (wantHidden) {
      setPhase('leaving');
      const t = setTimeout(() => setPhase('hidden'), HIDE_MS);
      return () => clearTimeout(t);
    }
    // hidden יורד יחד עם מצב ההתחלה (מחוץ למסך); אחרי שהדפדפן צייר אותו — המעבר למקום.
    setPhase('entering');
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setPhase('shown'));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [wantHidden]);
  return phase;
}

export function BottomBar({
  tab,
  onSelect,
  sentinel,
}: {
  tab: Tab;
  onSelect: (tab: Tab) => void;
  /** sentinel בסוף התוכן (App.tsx): כשהוא מתחת לסרגל — יש תוכן מתחתיו, ומופיע צל. */
  sentinel: RefObject<HTMLElement | null>;
}): ReactElement {
  const { occupied, forceHidden, setSlot } = useContext(BarStateContext);
  const stuck = useStuck(sentinel, '0px 0px -56px 0px');
  const phase = useBarPhase(useTextFocus() || forceHidden);
  const classes = [
    'action-bar',
    'material',
    stuck ? 'is-stuck' : '',
    phase === 'leaving' ? 'action-bar-leaving' : '',
    phase === 'entering' ? 'action-bar-entering' : '',
  ]
    .filter((c) => c !== '')
    .join(' ');
  return (
    <nav
      ref={setSlot}
      className={classes}
      aria-label="סרגל תחתון"
      hidden={phase === 'hidden'}
      data-testid="action-bar"
    >
      {!occupied && (
        <div
          className="action-bar-tabs"
          role="tablist"
          aria-label="מסכים"
          onKeyDown={(e) => moveWithArrows(e, TAB_ITEMS)}
        >
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              className="action-bar-btn"
              aria-selected={tab === t.id}
              aria-current={tab === t.id ? 'page' : undefined}
              tabIndex={tab === t.id ? 0 : -1}
              onClick={() => onSelect(t.id)}
              data-testid={t.testId}
            >
              <Icon name={t.icon} />
              <span>{t.label}</span>
            </button>
          ))}
        </div>
      )}
    </nav>
  );
}
